import {useEffect, useRef, useSyncExternalStore} from "react";
import type {MergeableStore as UntypedMergeableStore} from "tinybase";
import {createWsSynchronizer} from "tinybase/synchronizers/synchronizer-ws-client/with-schemas";
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/only-throw-error */
import type {
  Id,
  Store,
  TablesSchema,
  Values,
  ValuesSchema,
  Queries,
  Indexes,
  MergeableStore,
  OptionalSchemas,
} from "tinybase/with-schemas";
import {createIndexes, createMergeableStore, createQueries} from "tinybase/with-schemas";
import {create} from "zustand";
import {onNewerDataVersion} from "../manageServiceWorker";
import type {Nominal} from "../types";
import {capitalize} from "../utils";
import {createRepoStorage, mergeStored, type RepoStorage} from "./repo-storage";
import {TableSnapshots, ValuesSnapshot} from "./store-snapshots";
import {SYNC_REQUEST_TIMEOUT_SECONDS} from "./sync-timeout";
import {useSync} from "./use-evergreen-sync";

export type ModelId<TTableName extends string> = Nominal<Id, TTableName>;

const enum RepoStateType {
  IDLE,
  LOADING,
  LOADED,
  FAILED,
}
type RepoState<T> =
  | [RepoStateType.IDLE]
  | [RepoStateType.LOADING, Promise<T>]
  | [RepoStateType.LOADED, T]
  | [RepoStateType.FAILED, unknown];

const VERSION_KEY = "$version";

// repos whose synced data was migrated by a newer app version while this one was running
const useOutdatedRepos = create<{[key: string]: boolean | undefined}>(() => ({}));

type SyncInfo<
  TBuildInfo extends BuildInfo,
  TValueSchema extends ValuesSchema,
  TValuesModel extends Record<string, any>,
  TCtx,
  TSchema extends OptionalSchemas = [
    {[K in keyof TBuildInfo]: TBuildInfo[K]["schema"]},
    TValueSchema,
  ],
> = {
  get: (store: MergeableStore<TSchema>, ctx: TCtx) => string | null;
  use: (repo: LoadedRepo<TBuildInfo, TValueSchema, TValuesModel, TCtx>, ctx: TCtx) => string | null;
};

type LoadedRepo<
  TBuildInfo extends BuildInfo,
  TValueSchema extends ValuesSchema,
  TValuesModel extends Record<string, any>,
  TCtx,
  TSchema extends OptionalSchemas = [
    {[K in keyof TBuildInfo]: TBuildInfo[K]["schema"]},
    TValueSchema,
  ],
> = {
  vals: LoadedValues<TValuesModel>;
  store: MergeableStore<TSchema>;
  queries: Queries<TSchema>;
  indexes: Indexes<TSchema>;
  syncInfo: SyncInfo<TBuildInfo, TValueSchema, TValuesModel, TCtx>;
} & LoadedTable<TBuildInfo>;

type RepoOpts<
  TBuildInfo extends BuildInfo,
  TValueSchema extends ValuesSchema,
  TValuesModel extends Record<string, any>,
  TCtx,
  TSchema extends OptionalSchemas = [
    {[K in keyof TBuildInfo]: TBuildInfo[K]["schema"]},
    TValueSchema,
  ],
> = {
  key: string;
  version: number;
  values: DefinedValues<TValueSchema, TValuesModel>;
  tables: TableBuilder<TBuildInfo>;
  setUpQueries?: (queries: Queries<TSchema>) => void;
  setUpIndexes?: (indexes: Indexes<TSchema>) => void;
  syncInfo: SyncInfo<TBuildInfo, TValueSchema, TValuesModel, TCtx>;
  migrate: (
    repo: LoadedRepo<TBuildInfo, TValueSchema, TValuesModel, TCtx>,
    fromVersion: number
  ) => void;
  setupHooks?: (store: Store<TSchema>) => void;
};

// Data written from now on has this version's shape, so it has to be recorded: without a
// `$version`, a later app version takes the data for brand new and skips its migrations. But the
// repo may only look new because it's empty here, e.g. we're offline or loaded it without a sync
// url, while another device stamped an older version that still needs migrating. So the stamp gets
// an empty timestamp, which loses to any version a device actually stamped, however old.
const stampVersionIfMissing = (store: UntypedMergeableStore, version: number) => {
  if (store.getValue(VERSION_KEY) !== undefined) return;
  store.applyMergeableChanges([[{}], [{[VERSION_KEY]: [version]}], 1]);
};

const setupCache = () => {
  const cache = new Map<string, any>();
  return {
    getOr: <T>(key: string, fn: () => T): T => {
      const val = cache.get(key);
      if (val) return val;
      const newVal = fn();
      cache.set(key, newVal);
      return newVal;
    },
  };
};
const repoCache = setupCache();

export const defineRepo = <
  TBuildInfo extends BuildInfo,
  TValueSchema extends ValuesSchema,
  TValuesModel extends Record<string, any>,
  TCtx,
>(
  opts: RepoOpts<TBuildInfo, TValueSchema, TValuesModel, TCtx>
) => repoCache.getOr(opts.key, () => innerDefineRepo(opts));

const innerDefineRepo = <
  TBuildInfo extends BuildInfo,
  TValueSchema extends ValuesSchema,
  TValuesModel extends Record<string, any>,
  TCtx,
>(
  opts: RepoOpts<TBuildInfo, TValueSchema, TValuesModel, TCtx>
) => {
  const {
    key,
    version: targetVersion,
    tables,
    values,
    migrate,
    setUpQueries,
    setUpIndexes,
    syncInfo,
    setupHooks,
  } = opts;
  const store = createMergeableStore().setSchema(
    Object.fromEntries(
      Object.entries(tables.schema).map(([tableName, {schema}]) => [tableName, schema])
    ),
    // the assertion narrows the inferred values schema, so `$version` is typed as a number
    {
      ...values.schema,
      [VERSION_KEY]: {type: "number"},
    } as {[VERSION_KEY]: {type: "number"}}
  );
  const queries = createQueries(store);
  const indexes = createIndexes(store);
  if (setUpQueries) setUpQueries(queries as any);
  if (setUpIndexes) setUpIndexes(indexes as any);
  // once per store: the accessors' hooks keep listeners on it
  const loadedVal: LoadedRepo<TBuildInfo, TValueSchema, TValuesModel, TCtx> = {
    vals: values.setup(store as Store<[any, any]>),
    store,
    queries,
    indexes,
    ...tables.setup(store as Store<[any, any]>),
    syncInfo,
  } as any;
  let areHooksSetup = false;
  const untypedStore = store as unknown as UntypedMergeableStore;
  let storage: Promise<RepoStorage> | undefined;
  const getStorage = async () => (storage ??= createRepoStorage(untypedStore, key));
  let loadedState: RepoState<LoadedRepo<TBuildInfo, TValueSchema, TValuesModel, TCtx>> = [
    RepoStateType.IDLE,
  ];
  const load = (ctx: TCtx, retry = false) => {
    if (
      loadedState[0] === RepoStateType.IDLE ||
      (retry && loadedState[0] === RepoStateType.FAILED)
    ) {
      const promise = mergeStored(untypedStore, key).then(async () => {
        if (!areHooksSetup) {
          areHooksSetup = true;
          setupHooks?.(store as Store<[any, any]>);
          // a device running a newer app version migrated this data while we're running.
          // Stop syncing so we don't keep writing the old data shape, and ask for a reload.
          store.addValueListener(VERSION_KEY, (_store, _valueId, newVersion) => {
            // written as a negation so a deleted (undefined) value also bails out
            if (!(newVersion > targetVersion)) return;
            if (useOutdatedRepos.getState()[key]) return;
            useOutdatedRepos.setState({[key]: true});
            onNewerDataVersion();
          });
        }
        let loadedVersion = store.getValue(VERSION_KEY) ?? 0;
        if (loadedVersion < targetVersion) {
          const url = syncInfo.get(store as any, ctx);
          const didSync = url ? await syncBeforeMigrating(url, store as any) : false;
          if (didSync) {
            loadedVersion = store.getValue(VERSION_KEY) ?? targetVersion;
          } else if (loadedVersion === 0) {
            // brand new local db, no need to migrate
            loadedVersion = targetVersion;
          }
          // otherwise we're offline: migrate locally. This relies on migrations producing the
          // same rows on every device (see CLAUDE.md), so they merge once we're back online.
        }
        if (loadedVersion > targetVersion) {
          alert("Loaded data is from a newer version of the app. Please update the app.");
          throw new Error("Needs update");
        }
        if (loadedVersion < targetVersion) {
          store.transaction(() => {
            for (let i = loadedVersion; i < targetVersion; i++) {
              console.warn(`Migrate '${key}' from version ${i} to ${i + 1}`);
              migrate(loadedVal, i);
              store.setValue(VERSION_KEY, i + 1);
            }
          });
          await (await getStorage()).save();
        }
        stampVersionIfMissing(untypedStore, targetVersion);
        // only now: until here the data may still be in an older shape
        loadedState = [RepoStateType.LOADED, loadedVal];
        return loadedVal;
      });
      // e.g. storage couldn't be read. Suspense would keep waiting on a rejected promise, so
      // components get the error instead, and withRepo tries again.
      promise.catch((e: unknown) => {
        loadedState = [RepoStateType.FAILED, e];
      });
      loadedState = [RepoStateType.LOADING, promise];
    }
    return loadedState;
  };

  const useLoadedRepo = (ctx: TCtx) => {
    const val = load(ctx);
    switch (val[0]) {
      case RepoStateType.LOADED:
        return val[1];
      case RepoStateType.FAILED:
        throw val[1];
      case RepoStateType.LOADING:
        throw val[1];
    }
  };

  // set while a component uses the repo: then it's saved on every change and kept in sync with
  // other tabs. Otherwise what's stored may be newer than what's in memory.
  let stopStorage: (() => Promise<void>) | null = null;
  let consumerState:
    | [number, RepoStateType.IDLE]
    | [number, RepoStateType.LOADING, Promise<void>]
    | [number, RepoStateType.LOADED]
    | [number, RepoStateType.FAILED, unknown] = [0, RepoStateType.IDLE];
  let runningWithRepos = 0;

  const useReady = () => {
    switch (consumerState[1]) {
      case RepoStateType.IDLE: {
        const promise = (async () => {
          // what's stored may be newer: another tab or the service worker may have saved since
          // load() or since the last component stopped using the repo
          await mergeStored(untypedStore, key);
          stopStorage = await (await getStorage()).start();
          consumerState = [consumerState[0], RepoStateType.LOADED];
        })().catch((e: unknown) => {
          // Like a failed load, this sticks until the page reloads. Going back to IDLE would make
          // React's retry after the rejection start over, again and again for a lasting error.
          consumerState = [consumerState[0], RepoStateType.FAILED, e];
        });
        consumerState = [consumerState[0], RepoStateType.LOADING, promise];
        throw promise;
      }
      case RepoStateType.LOADING: {
        throw consumerState[2];
      }
      case RepoStateType.FAILED: {
        throw consumerState[2];
      }
      case RepoStateType.LOADED: {
        return;
      }
    }
  };

  // Writes while no component uses the repo (e.g. an upload that finishes after the user left the
  // group) would otherwise only be in memory until a component starts the storage again. Same while
  // it starts: autosave only listens once its first save is done.
  let isSavingIdle = false;
  let hasIdleChanges = false;
  const saveIdleChanges = async () => {
    if (isSavingIdle) return;
    isSavingIdle = true;
    try {
      // saving merges what's stored first, which can change the store and set this again
      while (hasIdleChanges) {
        hasIdleChanges = false;
        await (await getStorage()).save();
      }
    } finally {
      isSavingIdle = false;
    }
  };
  store.addDidFinishTransactionListener(() => {
    // load() and withRepo save when they're done
    if (loadedState[0] !== RepoStateType.LOADED || runningWithRepos > 0) return;
    if (consumerState[1] === RepoStateType.LOADED) return;
    const [changedTables, changedValues] = store.getTransactionChanges();
    if (Object.keys(changedTables).length === 0 && Object.keys(changedValues).length === 0) return;
    hasIdleChanges = true;
    saveIdleChanges().catch((e: unknown) => console.warn(`saving '${key}' failed`, e));
  });

  return {
    withRepo: async <T>(
      ctx: TCtx,
      cb: (repo: LoadedRepo<TBuildInfo, TValueSchema, TValuesModel, TCtx>) => Promise<T>
    ) => {
      const val = load(ctx, true);
      if (val[0] === RepoStateType.FAILED) throw val[1];
      const repo = val[0] === RepoStateType.LOADED ? val[1] : await val[1];
      runningWithRepos += 1;
      try {
        if (val[0] === RepoStateType.LOADED && consumerState[1] !== RepoStateType.LOADED) {
          await mergeStored(untypedStore, key);
        }
        const result = await cb(repo);
        await (await getStorage()).save();
        return result;
      } finally {
        runningWithRepos -= 1;
      }
    },
    useRepo: (ctx: TCtx) => {
      const repo = useLoadedRepo(ctx);
      useReady();
      useEffect(() => {
        consumerState[0] += 1;
        return () => {
          consumerState[0] -= 1;
          if (consumerState[0] === 0) {
            setTimeout(() => {
              if (consumerState[0] > 0) return;
              void stopStorage?.();
              stopStorage = null;
              consumerState = [0, RepoStateType.IDLE];
            }, 1000);
          }
        };
      }, [repo]);
      const syncInfo = repo.syncInfo.use(repo, ctx);
      const isOutdated = useOutdatedRepos((s) => s[key] ?? false);
      useSync({store: repo.store, url: isOutdated ? null : syncInfo, key});
      return repo;
    },
  };
};

type BuildInfo = Record<
  string,
  {
    schema: TablesSchema[string];
    _model: Record<string, any>;
  }
>;

type LoadedTable<TBuildInfo extends BuildInfo> = {
  [K in keyof TBuildInfo & string as Capitalize<K>]: {
    create: (item: TBuildInfo[K]["_model"]) => ModelId<K>;
    set: (id: ModelId<K>, item: TBuildInfo[K]["_model"]) => void;
    patch: (id: ModelId<K>, changes: Partial<Omit<TBuildInfo[K]["_model"], "id">>) => void;
    delete: (id: ModelId<K>) => void;
    getById: (id: ModelId<K>) => TBuildInfo[K]["_model"] | null;
    useById: (id: ModelId<K>) => TBuildInfo[K]["_model"] | null;
    useAllByIds: (ids: ModelId<K>[]) => TBuildInfo[K]["_model"][];
    getAll: () => Record<ModelId<K>, TBuildInfo[K]["_model"]>;
    useGetAllIds: () => ModelId<K>[];
    useGetAll: () => Record<ModelId<K>, TBuildInfo[K]["_model"]>;
    hydrate: (row: Values<TBuildInfo[K]["schema"]>) => TBuildInfo[K]["_model"];
  };
};

type TableOpts<TTableSchema extends TablesSchema[string], TModel extends Record<string, any>> = {
  dehydrate: (model: TModel) => Values<TTableSchema>;
  hydrate: (row: Values<TTableSchema>) => TModel;
};

type TableBuilder<TBuildInfo extends BuildInfo> = {
  schema: TBuildInfo;
  add: <
    TTableName extends string,
    TTableSchema extends TablesSchema[TTableName],
    TModel extends Record<string, any>,
  >(
    tableName: TTableName,
    schema: TTableSchema,
    opts: TableOpts<TTableSchema, TModel>
  ) => TableBuilder<
    TBuildInfo & {
      [K in TTableName]: {schema: TTableSchema; _model: TModel};
    }
  >;
  setup: (
    store: Store<[{[K in keyof TBuildInfo]: TBuildInfo[K]["schema"]}, any]>
  ) => LoadedTable<TBuildInfo>;
};

const loadTable = (
  store: Store<[any, any]>,
  name: string,
  opts: TableOpts<any, any>
): LoadedTable<any>[any] => {
  const {dehydrate, hydrate} = opts;
  type TModel = ReturnType<typeof hydrate>;
  const snapshots = new TableSnapshots<TModel>(store, name, hydrate);
  const getById = (id: ModelId<typeof name>) => {
    if (store.hasRow(name, id)) {
      const row = store.getRow(name, id);
      return hydrate(row);
    } else {
      return null;
    }
  };
  const getAll = () => {
    const table = store.getTable(name);
    return Object.fromEntries(
      Object.entries(table).map(([id, row]) => [id as ModelId<typeof name>, hydrate(row)])
    );
  };
  return {
    create: (item) => {
      return store.addRow(name, dehydrate(trimStringValues(item)), false) as ModelId<typeof name>;
    },
    set: (id, item) => {
      store.setRow(name, id, dehydrate(trimStringValues(item)));
    },
    patch: (id, changes) => {
      // e.g. another device deleted the row: writing the cells would leave a half-filled one
      const current = getById(id);
      if (!current) return;
      store.transaction(() => {
        writePatch(dehydrate, current, changes, {
          set: (cellId, val) => {
            store.setCell(name, id, cellId, val);
          },
          del: (cellId) => {
            store.delCell(name, id, cellId);
          },
        });
      });
    },
    delete: (id) => {
      store.delRow(name, id);
    },
    getById,
    getAll,
    useById: (id) => useSyncExternalStore(snapshots.subscribe, () => snapshots.getRow(id)),
    useAllByIds: (ids) => {
      const lastRef = useRef<TModel[]>([]);
      return useSyncExternalStore(snapshots.subscribe, () => {
        const rows = ids.map((id) => snapshots.getRow(id)).filter((row) => row !== null);
        // a new array on every call would make useSyncExternalStore re-render forever
        const last = lastRef.current;
        if (rows.length === last.length && rows.every((row, i) => row === last[i])) return last;
        lastRef.current = rows;
        return rows;
      });
    },
    useGetAllIds: () =>
      useSyncExternalStore(snapshots.subscribe, snapshots.getIds) as ModelId<typeof name>[],
    useGetAll: () => useSyncExternalStore(snapshots.subscribe, snapshots.getSnapshot),
    hydrate,
  };
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const defineTables = (): TableBuilder<{}> => {
  const schema: TablesSchema = {};
  const setupInfo: {name: string; opts: TableOpts<any, any>}[] = [];
  const builder: TableBuilder<any> = {
    schema,
    add: (tableName, tableSchema, opts) => {
      schema[tableName] = tableSchema;
      setupInfo.push({name: tableName, opts});
      return builder;
    },
    setup: (store) => {
      return Object.fromEntries(
        setupInfo.map(({name, opts}) => [capitalize(name), loadTable(store, name, opts)])
      );
    },
  };
  return builder as any;
};

type LoadedValues<TModel extends Record<string, any>> = {
  patch: (changes: Partial<TModel>) => void;
  get: () => TModel;
  useValues: () => TModel;
};

type DefinedValues<TValuesSchema extends ValuesSchema, TModel extends Record<string, any>> = {
  schema: TValuesSchema;
  setup: (store: Store<[any, TValuesSchema]>) => LoadedValues<TModel>;
};

type DefineValuesFn = <
  TValuesSchema extends ValuesSchema,
  TModel extends Record<string, any>,
>(opts: {
  schema: TValuesSchema;
  hydrate: (row: Values<TValuesSchema>) => TModel;
  dehydrate: (model: TModel) => Values<TValuesSchema>;
}) => DefinedValues<TValuesSchema, TModel>;

// Writes only the cells for the keys in `changes`, so cells it doesn't mention are left alone: a
// change another device made since the caller read the model, or a cell that's in the schema but
// that hydrate doesn't map. The merge with the current model is only there so `dehydrate` gets a
// whole model. A key whose cell dehydrates to undefined (a null field) deletes the cell.
const writePatch = <TModel extends Record<string, any>>(
  dehydrate: (model: TModel) => Record<string, unknown>,
  current: TModel,
  changes: Partial<TModel>,
  write: {set: (cellId: string, val: any) => void; del: (cellId: string) => void}
) => {
  const trimmed = trimStringValues(changes);
  const cells = dehydrate({...current, ...trimmed});
  for (const key of Object.keys(trimmed)) {
    const val = cells[key];
    if (val === undefined) write.del(key);
    else write.set(key, val);
  }
};

// returns a copy: callers often pass a model straight from a hook, and those snapshots are shared
const trimStringValues = <T extends Record<string, any>>(vals: T): T => {
  const trimmed: Record<string, unknown> = {...vals};
  for (const key in trimmed) {
    const val = trimmed[key];
    if (typeof val === "string") trimmed[key] = val.trim();
  }
  return trimmed as T;
};

export const defineValues: DefineValuesFn = ({schema, hydrate, dehydrate}) => {
  return {
    schema,
    setup: (store) => {
      const snapshot = new ValuesSnapshot(store, hydrate);
      return {
        patch: (changes) => {
          store.transaction(() => {
            writePatch(dehydrate, hydrate(store.getValues()), changes, {
              set: (valueId, val) => {
                store.setValue(valueId, val);
              },
              del: (valueId) => {
                store.delValue(valueId);
              },
            });
          });
        },
        get: () => hydrate(store.getValues()),
        useValues: () => useSyncExternalStore(snapshot.subscribe, snapshot.getSnapshot),
      };
    },
  };
};

export const oneOffSync = async (
  url: string,
  store: MergeableStore<[any, any]>,
  opts: {timeoutMs?: number} = {}
) => {
  const ws = new WebSocket(url);
  const sync = async () => {
    const synchronizer = await createWsSynchronizer(store, ws, SYNC_REQUEST_TIMEOUT_SECONDS);
    try {
      await synchronizer.startSync();
    } finally {
      await synchronizer.destroy();
      ws.close();
    }
  };
  const {timeoutMs} = opts;
  if (!timeoutMs) return sync();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      ws.close();
      reject(new Error("Sync timed out"));
    }, timeoutMs);
  });
  try {
    await Promise.race([sync(), timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
};

const PRE_MIGRATION_SYNC_TIMEOUT_MS = 5000;

// Pull the latest state before migrating so we build on other devices' migrations. Being offline
// must not keep the app from opening, so failures just mean we migrate locally.
const syncBeforeMigrating = async (url: string, store: MergeableStore<[any, any]>) => {
  try {
    // eslint-disable-next-line no-console
    console.log("🔄 SYNC FROM EXISTING SOURCE");
    await oneOffSync(url, store, {timeoutMs: PRE_MIGRATION_SYNC_TIMEOUT_MS});
    // eslint-disable-next-line no-console
    console.log("✅ SYNC DONE");
    return true;
  } catch (e) {
    console.warn("Sync before migrating failed, continuing offline", e);
    return false;
  }
};
